import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import AdminShell from './AdminShell';
import { Plus, Edit3, Trash2, Save, ArrowLeft, Eye, EyeOff, Loader2, Lock, Unlock, Archive, ArchiveRestore } from 'lucide-react';

const TYPES = [
  { v: 'enquete', l: 'Enquête' },
  { v: 'dossier_etg', l: 'Dossier ETG' },
  { v: 'guide', l: 'Guide' },
  { v: 'news', l: 'Actu' },
];

function slugify(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 100);
}

export function AdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageShell><div className="max-w-7xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;
  if (!user) return <PageShell><div className="max-w-3xl mx-auto p-12 text-center"><Lock className="mx-auto text-red-400 mb-4" size={32} /><h2 className="font-serif-editorial text-white text-[28px]">Espace administrateur</h2><p className="text-white/55 mt-2">Connecte-toi avec un compte admin.</p></div></PageShell>;
  if (!user.is_admin) return <PageShell><div className="max-w-3xl mx-auto p-12 text-center"><Lock className="mx-auto text-red-400 mb-4" size={32} /><h2 className="font-serif-editorial text-white text-[28px]">Accès refusé</h2><p className="text-white/55 mt-2">Ton compte n’a pas les droits administrateur.</p></div></PageShell>;
  return children;
}

export function AdminArticlesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await api.adminListArticles()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    await api.adminDeleteArticle(id);
    load();
  };

  const archive = async (id) => {
    await api.adminArchiveArticle(id);
    load();
  };

  const unarchive = async (id) => {
    await api.adminUnarchiveArticle(id);
    load();
  };

  return (
    <AdminGuard>
      <AdminShell>
        <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <SectionTitle kicker="Admin · CMS" title="Gestion du magazine" sub="Crée, édite, publie et archive tes enquêtes, dossiers ETG, guides et actus." />
            <Link to="/admin/articles/new" data-testid="admin-new-article-btn" className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40"><Plus size={15} /> Nouvel article</Link>
          </div>
          {loading ? <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div> : (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid="admin-articles-table">
              <table className="w-full text-[13.5px]">
                <thead className="bg-white/[0.02] text-white/50 text-[11px] uppercase tracking-wider">
                  <tr><th className="text-left p-4">Titre</th><th className="text-left p-4 hidden md:table-cell">Type</th><th className="text-left p-4 hidden lg:table-cell">Slug</th><th className="text-left p-4 hidden md:table-cell">État</th><th className="text-right p-4">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {items.map((a) => (
                    <tr key={a.id} className="hover:bg-white/[0.02]" data-testid={`admin-article-row-${a.id}`}>
                      <td className="p-4 text-white">
                        <div className="font-semibold">{a.title}</div>
                        {a.issue_number && <div className="text-[11px] text-red-400 font-mono-spec mt-0.5">{a.issue_number}</div>}
                      </td>
                      <td className="p-4 hidden md:table-cell text-white/70">{(TYPES.find((t) => t.v === a.type) || {}).l || a.type}</td>
                      <td className="p-4 hidden lg:table-cell text-white/50 font-mono-spec text-[12px]">{a.slug}</td>
                      <td className="p-4 hidden md:table-cell">
                        {a.archived ? <span className="inline-flex items-center gap-1 text-amber-400 text-[12px] font-semibold"><Archive size={12} /> archivé</span>
                          : a.published ? <span className="inline-flex items-center gap-1 text-emerald-400 text-[12px] font-semibold"><Eye size={12} /> publié</span>
                          : <span className="inline-flex items-center gap-1 text-white/40 text-[12px]"><EyeOff size={12} /> brouillon</span>}
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex gap-1">
                          {a.archived ? (
                            <button data-testid={`unarchive-${a.id}`} onClick={() => unarchive(a.id)} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-emerald-700/30 text-emerald-400 hover:bg-emerald-950/30" title="Désarchiver"><ArchiveRestore size={14} /></button>
                          ) : (
                            <button data-testid={`archive-${a.id}`} onClick={() => archive(a.id)} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-amber-700/30 text-amber-400 hover:bg-amber-950/30" title="Archiver"><Archive size={14} /></button>
                          )}
                          <Link to={`/admin/articles/${a.id}`} data-testid={`edit-${a.id}`} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/30"><Edit3 size={14} /></Link>
                          <button data-testid={`delete-article-${a.id}`} onClick={() => remove(a.id)} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-red-700/30 text-red-400 hover:bg-red-950/30"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-white/50">Aucun article pour l’instant.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </AdminShell>
    </AdminGuard>
  );
}

export function AdminArticleEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === 'new';
  const [form, setForm] = useState({ slug: '', type: 'enquete', theme: '', title: '', subtitle: '', body_markdown: '', cover_image: '', read_time_min: 5, published: true, issue_number: '' });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [coverLocked, setCoverLocked] = useState(true);
  const [isMapped, setIsMapped] = useState(false);
  const [togglingLock, setTogglingLock] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.adminListArticles().then((all) => {
        const a = all.find((x) => x.id === id);
        if (a) {
          setForm({ slug: a.slug, type: a.type, theme: a.theme || '', title: a.title, subtitle: a.subtitle || '', body_markdown: a.body_markdown, cover_image: a.cover_image || '', read_time_min: a.read_time_min, published: a.published, issue_number: a.issue_number || '' });
          setCoverLocked(a.cover_locked !== false);
          setIsMapped(!!a.is_mapped);
        }
        setLoading(false);
      });
    }
  }, [id, isNew]);

  const toggleCoverLock = async () => {
    setTogglingLock(true);
    try {
      const res = await api.adminToggleCoverLock(id);
      setCoverLocked(res.cover_locked);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur toggle cover lock');
    } finally {
      setTogglingLock(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.slug && payload.title) payload.slug = slugify(payload.title);
      payload.theme = payload.theme || null;
      payload.issue_number = payload.issue_number || null;
      if (isNew) {
        const created = await api.adminCreateArticle(payload);
        nav(`/admin/articles/${created.id}`);
      } else {
        await api.adminUpdateArticle(id, payload);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminGuard><AdminShell><div className="max-w-3xl mx-auto p-12 text-white/50 inline-flex gap-2 items-center"><Loader2 size={16} className="animate-spin" /> Chargement…</div></AdminShell></AdminGuard>;

  return (
    <AdminGuard>
      <AdminShell>
        <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-10">
          <Link to="/admin/articles" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-[13px] mb-6"><ArrowLeft size={14} /> Retour</Link>
          <SectionTitle kicker={isNew ? 'Nouvel article' : 'Modifier l’article'} title={form.title || 'Sans titre'} />
          <div className="grid lg:grid-cols-[2fr_1fr] gap-6 mt-8">
            <div className="space-y-4">
              <Field label="Titre"><input value={form.title} onChange={(e) => update('title', e.target.value)} onBlur={() => !form.slug && update('slug', slugify(form.title))} className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500" /></Field>
              <Field label="Sous-titre"><input value={form.subtitle} onChange={(e) => update('subtitle', e.target.value)} className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500" /></Field>
              <Field label="Contenu (markdown : # titres, **gras**, _italique_, - listes, > citations)">
                <textarea rows={20} value={form.body_markdown} onChange={(e) => update('body_markdown', e.target.value)} className="w-full p-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 font-mono-spec" />
              </Field>
            </div>
            <div className="space-y-4">
              <Field label="Type">
                <select value={form.type} onChange={(e) => update('type', e.target.value)} className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500">
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Field>
              <Field label="Thème (optionnel)"><input value={form.theme} onChange={(e) => update('theme', e.target.value)} placeholder="ex: La mécanique" className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500" /></Field>
              <Field label="Numéro"><input value={form.issue_number} onChange={(e) => update('issue_number', e.target.value)} placeholder="ex: N°31" className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 font-mono-spec" /></Field>
              <Field label="Slug (URL)"><input value={form.slug} onChange={(e) => update('slug', slugify(e.target.value))} className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 font-mono-spec" /></Field>
              <Field label="Image de couverture (URL)">
                <input value={form.cover_image} onChange={(e) => update('cover_image', e.target.value)} className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500" data-testid="admin-cover-input" />
                {!isNew && isMapped && (
                  <div className={`mt-2 rounded-lg border p-2.5 text-[11.5px] ${coverLocked ? 'border-amber-500/40 bg-amber-950/20' : 'border-emerald-500/40 bg-emerald-950/20'}`}>
                    <div className="flex items-start gap-2">
                      {coverLocked ? <Lock size={13} className="text-amber-400 mt-0.5 shrink-0" /> : <Unlock size={13} className="text-emerald-400 mt-0.5 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${coverLocked ? 'text-amber-300' : 'text-emerald-300'}`}>
                          {coverLocked ? 'Cover verrouillée' : 'Cover débloquée'}
                        </div>
                        <div className="text-white/60 leading-snug mt-0.5">
                          {coverLocked
                            ? "Ce slug figure dans ARTICLE_COVER_MAP côté backend. À chaque redémarrage, l'image est forcée à l'URL canonique — tes changements seront écrasés."
                            : "L'image ne sera plus écrasée au démarrage. Tu peux la modifier librement ici."}
                        </div>
                        <button
                          type="button"
                          onClick={toggleCoverLock}
                          disabled={togglingLock}
                          data-testid="admin-cover-toggle-lock"
                          className={`mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11.5px] font-semibold transition ${coverLocked ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-amber-600 hover:bg-amber-500 text-white'} disabled:opacity-50`}
                        >
                          {togglingLock ? <Loader2 size={11} className="animate-spin" /> : coverLocked ? <Unlock size={11} /> : <Lock size={11} />}
                          {coverLocked ? 'Débloquer la cover' : 'Reverrouiller la cover'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </Field>
              <Field label="Temps de lecture (min)"><input type="number" min={1} max={120} value={form.read_time_min} onChange={(e) => update('read_time_min', parseInt(e.target.value) || 5)} className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500" /></Field>
              <label className="flex items-center gap-2 text-white/80 text-[13.5px]">
                <input type="checkbox" checked={form.published} onChange={(e) => update('published', e.target.checked)} className="w-4 h-4 accent-red-600" />
                Publier (visible publiquement)
              </label>
              {error && <div className="text-red-400 text-[12.5px] bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">{error}</div>}
              <button onClick={save} disabled={saving} className="w-full h-12 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={15} />} {isNew ? 'Créer' : 'Enregistrer'}</button>
              {!isNew && <Link to={`/magazine/${form.slug}`} target="_blank" className="block text-center text-[12px] text-white/40 hover:text-white">Prévisualiser →</Link>}
            </div>
          </div>
        </section>
      </AdminShell>
    </AdminGuard>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
